import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MapPin, Phone, Mail, Linkedin, Facebook } from 'lucide-react'
import { supabase } from '@/lib/supabase'

const quickLinks = [
  { to: '/products', label: 'Products' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/certificates', label: 'Certificates' },
  { to: '/about', label: 'About Us' },
  { to: '/contact', label: 'Contact' },
]

const productCategories = [
  { to: '/products/doors', label: 'Doors' },
  { to: '/products/hatches', label: 'Hatches' },
  { to: '/products/pv-valves', label: 'PV Valves' },
  { to: '/products/ventilation', label: 'Ventilation' },
  { to: '/products/deck-equipment', label: 'Deck Equipment' },
]

interface ContactInfo {
  company_name?: string
  address?: string
  city?: string
  country?: string
  phone?: string
  email?: string
  footer_logo?: string
  footer_logo_link?: string
}

export function Footer() {
  const currentYear = new Date().getFullYear()

  const { data: contactInfo } = useQuery({
    queryKey: ['contact-info'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'contact_info')
        .single()
      if (error) return null
      return data.value as ContactInfo
    },
    staleTime: 60000,
  })

  return (
    <footer className="bg-secondary-900 text-secondary-300">
      <div className="container-custom py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <img
                src="/images/logo-white.png"
                alt="EngiMarineX"
                className="h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-sm leading-relaxed">
              Marine Engineering Technology - Providing high-quality doors, hatches,
              valves, and deck equipment for the maritime industry.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-secondary-800 rounded-lg hover:bg-secondary-700 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-secondary-800 rounded-lg hover:bg-secondary-700 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white font-semibold mb-4">Products</h4>
            <ul className="space-y-2">
              {productCategories.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary-400" />
                <span className="text-sm">
                  {contactInfo?.address || 'Manastirski Livadi, Bulgaria Blvd 98'},
                  <br />
                  {contactInfo?.city || 'Sofia'}, {contactInfo?.country || 'Bulgaria'}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 flex-shrink-0 text-primary-400" />
                <a
                  href={`tel:${contactInfo?.phone?.replace(/\s/g, '') || '+359879891621'}`}
                  className="text-sm hover:text-white transition-colors"
                >
                  {contactInfo?.phone || '+359 879 89 16 21'}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 flex-shrink-0 text-primary-400" />
                <a
                  href={`mailto:${contactInfo?.email || 'contact@engitronix.com'}`}
                  className="text-sm hover:text-white transition-colors"
                >
                  {contactInfo?.email || 'contact@engitronix.com'}
                </a>
              </li>
            </ul>
            {/* Footer Contact Logo */}
            {contactInfo?.footer_logo && (
              <div className="mt-6">
                {contactInfo.footer_logo_link ? (
                  <a
                    href={contactInfo.footer_logo_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-lg ring-1 ring-white/20 hover:bg-white transition-colors"
                  >
                    <img
                      src={contactInfo.footer_logo}
                      alt="Partner logo"
                      className="w-full max-h-16 object-contain"
                    />
                  </a>
                ) : (
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-lg ring-1 ring-white/20">
                    <img
                      src={contactInfo.footer_logo}
                      alt="Partner logo"
                      className="w-full max-h-16 object-contain"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-800">
        <div className="container-custom py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-secondary-400">
            &copy; {currentYear} EngiMarineX. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-sm hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
